'use client'

import type { ForgotPasswordProps } from '@maestro/commerce-blocks/components/forgot-password/forgot-password.types'

import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'

import { useForgotPassword } from '@maestro/commerce-blocks/components/forgot-password/batons/use-forgot-password'
import { Button } from '@pearlconvert/ui/components/button'
import { SlideInBody } from '@pearlconvert/ui/components/slide-in'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

import '@maestro/commerce-blocks/components/forgot-password/styles/forgot-password.scss'

const ForgotPasswordForm = dynamic(
  () =>
    import('@maestro/commerce-blocks/components/forgot-password/forgot-password-form')
)
const ForgotPasswordConfirmation = dynamic(
  () =>
    import('@maestro/commerce-blocks/components/forgot-password/forgot-password-confirmation')
)

export default function ForgotPassword(props: ForgotPasswordProps) {
  const { cmsBlock, cmsBlockForgotPassword } = props
  const t = useTranslations('commerce-blocks')
  const { addModal } = useModals()
  const {
    forgotPasswordModalKey,
    handleOpenForgotPassword,
    handleResetSuccess,
    handleBackToLogin,
  } = useForgotPassword()

  useEffect(() => {
    addModal({
      key: forgotPasswordModalKey,
      hash: forgotPasswordModalKey,
      Component: (
        <SlideInBody>
          <ForgotPasswordForm
            onSuccess={handleResetSuccess}
            cmsBlock={cmsBlockForgotPassword}
          />
        </SlideInBody>
      ),
      props: {
        title: t('auth.forgot-password.title'),
        hasBackButton: true,
        slideInType: 'reset_password',
      },
    })

    addModal({
      key: 'forgot-password-confirmation',
      Component: (
        <SlideInBody>
          <ForgotPasswordConfirmation
            onBackToLogin={handleBackToLogin}
            cmsBlock={cmsBlock}
          />
        </SlideInBody>
      ),
      props: {
        title: t('auth.forgot-password.title'),
        hasBackButton: false,
      },
    })
  }, [
    addModal,
    cmsBlock,
    cmsBlockForgotPassword,
    forgotPasswordModalKey,
    handleBackToLogin,
    handleResetSuccess,
    t,
  ])

  return (
    <Button
      type="link"
      buttonType="button"
      onClick={handleOpenForgotPassword}
      className="forgot-password-button"
    >
      {t('auth.forgot-password.cta')}
    </Button>
  )
}
